import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronDown,
  ArrowRight,
  X,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ALL_OPEN_POSITIONS } from "../data/openPositionsData";

const OpenPositions = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDept, setSelectedDept] = useState("All");
  const [selectedLocation, setSelectedLocation] = useState("All");
  const [selectedExperience, setSelectedExperience] = useState("All");
  const [selectedWorkMode, setSelectedWorkMode] = useState("All");
  const [sortBy, setSortBy] = useState("relevant");

  const [selectedRoleForModal, setSelectedRoleForModal] = useState(null);
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    message: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredPositions = ALL_OPEN_POSITIONS.filter((pos) => {
    const query = searchQuery.toLowerCase().trim();
    const matchesSearch =
      query === "" ||
      pos.title.toLowerCase().includes(query) ||
      pos.dept.toLowerCase().includes(query) ||
      pos.desc.toLowerCase().includes(query) ||
      (pos.tags && pos.tags.some((t) => t.toLowerCase().includes(query))) ||
      (pos.skills && pos.skills.some((s) => s.toLowerCase().includes(query)));

    const matchesDept =
      selectedDept === "All" ||
      pos.dept.toLowerCase().includes(selectedDept.toLowerCase());

    const matchesLoc =
      selectedLocation === "All" ||
      pos.loc.toLowerCase().includes(selectedLocation.toLowerCase());

    const matchesExp =
      selectedExperience === "All" ||
      pos.experience.toLowerCase().includes(selectedExperience.toLowerCase());

    const matchesMode =
      selectedWorkMode === "All" ||
      pos.workMode.toLowerCase().includes(selectedWorkMode.toLowerCase());

    return (
      matchesSearch &&
      matchesDept &&
      matchesLoc &&
      matchesExp &&
      matchesMode
    );
  }).sort((a, b) => {
    if (sortBy === "newest") {
      return (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0);
    }
    return 0;
  });

  const handleRoleClick = (role) => {
    navigate(role.link || `/careers/${role.id}`);
  };

  const handleApplySubmit = (e) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    if (formData.phone && !/^\d{10}$/.test(formData.phone.trim())) {
      toast.error("Please enter a valid 10-digit phone number.");
      return;
    }
    toast.success(
      `Application submitted for ${selectedRoleForModal?.title || "the role"}! We'll review your details shortly.`
    );
    setFormData({ fullName: "", phone: "", email: "", message: "" });
    setSelectedRoleForModal(null);
  };

  return (
    <div className="min-h-screen w-full bg-white">
      <Navbar />

      <main>
        {/* Hero Section - exact Figma inspect: 1,440px width, 533px height, Top: 80px below Navbar, rgba(1, 7, 16, 1) */}
        <section
          className="relative w-full overflow-hidden mt-[76px] sm:mt-[80px] lg:h-[533px]"
          style={{ backgroundColor: "rgba(1, 7, 16, 1)" }}
        >
          {/* 1,440px Centered Canvas Container */}
          <div className="relative mx-auto max-w-[1440px] w-full h-full min-h-[533px]">
            {/* Panoramic Office Team Photo on Right - Full Bleed 533px height */}
            <div className="absolute inset-y-0 right-0 h-full w-full lg:w-[54%] overflow-hidden pointer-events-none">
              <img
                src="/images/careers/open-positions-hero.png"
                alt="Simpel AI Team"
                className="h-full w-full object-cover object-[center_top]"
              />
              {/* Linear Gradient matching Figma - fades smoothly into rgba(1, 7, 16, 1) on left */}
              <div
                className="absolute inset-0 hidden lg:block"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(1, 7, 16, 1) 0%, rgba(1, 7, 16, 0.95) 14%, rgba(1, 7, 16, 0.42) 38%, rgba(1, 7, 16, 0) 65%)",
                }}
              />
              {/* Top gradient overlay for seamless navbar blending */}
              <div
                className="absolute inset-x-0 top-0 h-24 pointer-events-none hidden lg:block"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(1, 7, 16, 0.85) 0%, rgba(1, 7, 16, 0) 100%)",
                }}
              />
              {/* Mobile / Tablet dark overlay */}
              <div
                className="absolute inset-0 lg:hidden"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(1, 7, 16, 0.95) 0%, rgba(1, 7, 16, 0.78) 50%, rgba(1, 7, 16, 0.95) 100%)",
                }}
              />
            </div>

            {/* Ambient Blue Background Glow on Left */}
            <div
              className="pointer-events-none absolute"
              style={{
                width: 750,
                height: 440,
                left: -80,
                top: 40,
                background: "radial-gradient(circle, #173862 0%, rgba(1, 7, 16, 0) 70%)",
                filter: "blur(90px)",
                opacity: 0.65,
              }}
            />

            {/* Left Text Content - aligned with left 63px */}
            <div className="relative z-10 px-6 lg:px-0 lg:absolute lg:left-[63px] lg:top-[60px] max-w-[540px] pt-10 lg:pt-0">
              <p className="text-[11.5px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#8E9CAB]">
                CAREERS
              </p>

              <h1 className="mt-3 text-[42px] sm:text-[50px] lg:text-[56px] font-bold text-white tracking-tight leading-[1.08]">
                Open Positions
              </h1>

              <p className="mt-3.5 text-[15px] sm:text-[16px] leading-[25px] text-[#94A3B8] max-w-[500px]">
                Join us in building intelligent solutions that create real impact. Explore opportunities across our teams and find a role where you can do your best work.
              </p>
            </div>

            {/* SearchFilterSection - exact Figma inspect:
                Width: Fixed (1,314px)
                Height: Hug (76px)
                Top: 402px
                Left: 63px
                Radius: 16px
                Border: 1px rgba(226, 232, 240, 0.8)
                Padding: 16px
                Color: rgba(255, 255, 255, 1)
            */}
            <div
              id="searchfiltersection"
              className="relative z-20 mx-4 sm:mx-6 lg:mx-0 mt-8 mb-6 lg:mt-0 lg:mb-0 lg:absolute lg:top-[402px] lg:left-[63px] lg:w-[1314px] min-h-[76px] rounded-[16px] p-4 flex flex-col lg:flex-row items-center gap-3"
              style={{
                backgroundColor: "rgba(255, 255, 255, 1)",
                border: "1px solid rgba(226, 232, 240, 0.8)",
                boxShadow:
                  "0px 18px 45px rgba(0, 194, 255, 0.35), 0px 4px 20px rgba(0, 0, 0, 0.14)",
              }}
            >
              {/* Search input with blue accent */}
              <div className="relative w-full lg:flex-[2.2] min-w-0">
                <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#0091FF]" />
                <input
                  type="text"
                  placeholder="Search by role, skill or keyword..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-[44px] w-full rounded-[10px] sm:rounded-[12px] border-2 border-[#0091FF] bg-white pl-11 pr-4 text-[13.5px] text-[#0F172A] placeholder:text-[#94A3B8] outline-none transition-all shadow-[0_0_12px_rgba(0,145,255,0.18)]"
                />
              </div>

              {/* Department filter */}
              <div className="relative w-full lg:flex-1 min-w-0">
                <select
                  value={selectedDept}
                  onChange={(e) => setSelectedDept(e.target.value)}
                  className="h-[44px] w-full appearance-none rounded-[10px] sm:rounded-[12px] border border-[#E2E8F0] bg-white pl-3.5 pr-8 text-[13.5px] font-medium text-[#475569] outline-none transition-colors hover:border-[#CBD5E1] cursor-pointer"
                >
                  <option value="All">Department</option>
                  <option value="AI & Engineering">AI & Engineering</option>
                  <option value="Product & Design">Product & Design</option>
                  <option value="Strategy & Consulting">Strategy & Consulting</option>
                  <option value="Data">Data</option>
                  <option value="Technology">Technology</option>
                  <option value="Marketing & Brand">Marketing & Brand</option>
                  <option value="People & Culture">People & Culture</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748B]" />
              </div>

              {/* Location filter */}
              <div className="relative w-full lg:flex-1 min-w-0">
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="h-[44px] w-full appearance-none rounded-[10px] sm:rounded-[12px] border border-[#E2E8F0] bg-white pl-3.5 pr-8 text-[13.5px] font-medium text-[#475569] outline-none transition-colors hover:border-[#CBD5E1] cursor-pointer"
                >
                  <option value="All">Location</option>
                  <option value="Bengaluru">Bengaluru / Hybrid</option>
                  <option value="Multiple">Multiple locations</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748B]" />
              </div>

              {/* Experience filter */}
              <div className="relative w-full lg:flex-1 min-w-0">
                <select
                  value={selectedExperience}
                  onChange={(e) => setSelectedExperience(e.target.value)}
                  className="h-[44px] w-full appearance-none rounded-[10px] sm:rounded-[12px] border border-[#E2E8F0] bg-white pl-3.5 pr-8 text-[13.5px] font-medium text-[#475569] outline-none transition-colors hover:border-[#CBD5E1] cursor-pointer"
                >
                  <option value="All">Experience</option>
                  <option value="Mid-Level">Mid-Level</option>
                  <option value="Mid-Senior">Mid-Senior</option>
                  <option value="Senior">Senior</option>
                  <option value="Lead">Lead</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748B]" />
              </div>

              {/* Work mode filter */}
              <div className="relative w-full lg:flex-1 min-w-0">
                <select
                  value={selectedWorkMode}
                  onChange={(e) => setSelectedWorkMode(e.target.value)}
                  className="h-[44px] w-full appearance-none rounded-[10px] sm:rounded-[12px] border border-[#E2E8F0] bg-white pl-3.5 pr-8 text-[13.5px] font-medium text-[#475569] outline-none transition-colors hover:border-[#CBD5E1] cursor-pointer"
                >
                  <option value="All">Work mode</option>
                  <option value="Hybrid">Hybrid</option>
                  <option value="Remote">Remote</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748B]" />
              </div>
            </div>
          </div>
        </section>

        {/* Listings Section - exact background rgba(245, 250, 254, 1) */}
        <section
          className="w-full pt-12 pb-24 lg:pt-14 lg:pb-28"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[63px]">
            {/* Header row: Count + Sort */}
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-[18px] sm:text-[20px] font-bold text-[#0F172A]">
                {filteredPositions.length} open positions
              </h2>

              <div className="flex items-center gap-2 text-[13.5px] text-[#64748B]">
                <span>Sort by</span>
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none rounded-[10px] border border-[#E2E8F0] bg-white px-3 py-1.5 pr-7 text-[13.5px] font-semibold text-[#0F172A] outline-none hover:border-[#CBD5E1] cursor-pointer"
                  >
                    <option value="relevant">Most relevant</option>
                    <option value="newest">Newest</option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-[#94A3B8]" />
                </div>
              </div>
            </div>

            {/* 3-Column Grid of Cards matching exact 3rd screenshot specs */}
            {filteredPositions.length === 0 ? (
              <div className="rounded-[18px] border border-[#E2E8F0] bg-white p-12 text-center">
                <h3 className="text-[18px] font-bold text-[#0F172A]">No positions found</h3>
                <p className="mt-2 text-[14px] text-[#64748B]">
                  Try adjusting your search or filters to see more results.
                </p>
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedDept("All");
                    setSelectedLocation("All");
                    setSelectedExperience("All");
                    setSelectedWorkMode("All");
                  }}
                  className="mt-5 rounded-full bg-[#007AFF] px-6 py-2.5 text-[13.5px] font-semibold text-white hover:bg-[#0056B3] transition-colors cursor-pointer"
                >
                  Reset all filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPositions.map((job) => (
                  <div
                    key={job.id}
                    onClick={() => handleRoleClick(job)}
                    className="group flex flex-col justify-between rounded-[16px] p-6 shadow-sm transition-all duration-300 hover:border-[#007AFF]/60 hover:shadow-md hover:-translate-y-1 cursor-pointer"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 1)",
                      border: "1px solid rgba(226, 232, 240, 0.9)",
                      borderRadius: "16px",
                      minHeight: "251.5px",
                    }}
                  >
                    {/* Top Content */}
                    <div className="flex flex-col gap-2">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-[18px] font-bold text-[#0F172A] leading-snug group-hover:text-[#007AFF] transition-colors">
                          {job.title}
                        </h3>
                        {job.isNew && (
                          <span className="shrink-0 rounded-full border border-[#BFDBFE] bg-[#EFF6FF] px-2.5 py-0.5 text-[11px] font-bold text-[#007AFF]">
                            New
                          </span>
                        )}
                      </div>

                      {/* Department | Location */}
                      <div className="flex flex-wrap items-center gap-1.5 text-[12.5px]">
                        <span className="font-semibold text-[#007AFF]">
                          {job.dept}
                        </span>
                        <span className="text-[#CBD5E1]">|</span>
                        <span className="text-[#64748B]">{job.loc}</span>
                      </div>

                      {/* Description */}
                      <p className="mt-1 text-[13px] sm:text-[13.5px] leading-[20px] text-[#64748B]">
                        {job.desc}
                      </p>
                    </div>

                    {/* Middle: Skill Tags */}
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {job.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center rounded-[8px] border border-[#E2E8F0] bg-[#F8FAFC] px-2.5 py-1 text-[11.5px] font-medium text-[#475569]"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Bottom Action */}
                    <div className="mt-5 pt-1">
                      <span className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-[#0066FF] group-hover:gap-2.5 transition-all">
                        View Role <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />

      {/* Role Application Modal */}
      <AnimatePresence>
        {selectedRoleForModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedRoleForModal(null)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm cursor-pointer"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 1.04, y: 15 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="relative z-10 w-full max-w-[520px] rounded-[26px] bg-white p-7 sm:p-9 shadow-2xl"
            >
              <button
                onClick={() => setSelectedRoleForModal(null)}
                className="absolute right-5 top-5 flex h-8 w-8 items-center justify-center rounded-full text-[#64748B] hover:bg-gray-100 hover:text-[#0F172A] transition-colors cursor-pointer"
                aria-label="Close modal"
              >
                <X className="h-4 w-4" />
              </button>

              <h3 className="text-[26px] sm:text-[28px] font-bold text-[#0F172A] leading-tight">
                Apply for {selectedRoleForModal.title}
              </h3>
              <p className="mt-2 text-[14px] sm:text-[15px] text-[#556376] leading-relaxed">
                Please fill out the form below with your details and message to contact us.
              </p>

              <form onSubmit={handleApplySubmit} className="mt-6 flex flex-col gap-3.5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={(e) =>
                      setFormData({ ...formData, fullName: e.target.value })
                    }
                    required
                    className="w-full rounded-[14px] bg-[#E5E7EB]/80 px-4 py-3.5 text-[14px] text-[#0F172A] placeholder:text-[#6B7280] outline-none transition-all focus:bg-[#E5E7EB] focus:ring-2 focus:ring-[#00D2EE]/40"
                  />
                  <div className="flex items-center rounded-[14px] bg-[#E5E7EB]/80 overflow-hidden focus-within:bg-[#E5E7EB] focus-within:ring-2 focus-within:ring-[#00D2EE]/40 transition-all">
                    <span className="px-3.5 text-[14px] font-semibold text-[#6B7280] border-r border-[#D1D5DB] select-none">
                      +91
                    </span>
                    <input
                      type="tel"
                      maxLength={10}
                      inputMode="numeric"
                      placeholder="10-digit phone number"
                      value={formData.phone}
                      onChange={(e) => {
                        const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10);
                        setFormData({ ...formData, phone: digitsOnly });
                      }}
                      className="w-full bg-transparent px-3.5 py-3.5 text-[14px] text-[#0F172A] placeholder:text-[#6B7280] outline-none"
                    />
                  </div>
                </div>

                <input
                  type="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                  className="w-full rounded-[14px] bg-[#E5E7EB]/80 px-4 py-3.5 text-[14px] text-[#0F172A] placeholder:text-[#6B7280] outline-none transition-all focus:bg-[#E5E7EB] focus:ring-2 focus:ring-[#00D2EE]/40"
                />

                <textarea
                  rows={4}
                  placeholder="Tell us about yourself..."
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  className="w-full resize-none rounded-[16px] bg-[#E5E7EB]/80 p-4 text-[14px] text-[#0F172A] placeholder:text-[#6B7280] outline-none transition-all focus:bg-[#E5E7EB] focus:ring-2 focus:ring-[#00D2EE]/40"
                />

                <div className="mt-2 flex justify-end">
                  <button
                    type="submit"
                    className="flex items-center gap-2 rounded-full px-7 py-3 text-[14.5px] font-bold text-[#0F172A] transition-all hover:scale-105 active:scale-95 shadow-md hover:shadow-lg cursor-pointer"
                    style={{
                      background:
                        "linear-gradient(90deg, #00D2EE 0%, #2563EB 100%)",
                    }}
                  >
                    Submit <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OpenPositions;
